// components/MyCatalogues/MyCatalogues.js
import React, { useState, useEffect, useCallback } from 'react';
import { Empty, Spin, App } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import SimpleCard from '../components/Components/Cards/SimpleCard';
import { secureGetItem } from '../data/utils';

// Custom hooks
import { useCatalogues } from '../services/hooks/useCatalogues';
import { useModalState } from '../services/hooks/useModalState';
import { useDraftManagement } from '../services/hooks/useDraftManagement';

// Utils and helpers
import { getQrCodeUrl, getStatCards, handleDownloadQrCode } from '../services/utils/catalogueHelpers';
import { saveDraftedCatalogue, updateDraftedCatalogue } from '../services/api/catalogueDraftService';
import { deleteDraftedCatalogue } from '../services/api/catalogueBaseService';
import { updateCatalogStatus } from '../services/api/catalogStatusService';
import { setSelectedCatalogues } from '../redux/Catalogues/actions';
import { statusTokens } from '../theme/theme';
import { notifyNewCatalogPublished } from '../services/emailService';
import { getSubscriptionIdByCatalogueId } from '../services/utils/subscriptionUtils';

// Components
import ToggleButtons from '../components/Components/Buttons/ToggleButtons';
import CardCatalog from '../components/MyCatalogues/CardCatalog';
import ConfirmationModal from '../components/MyCatalogues/Modals/ConfirmationModal';
import NewCatalogModal from '../components/MyCatalogues/Modals/NewCatalogModal';
import ConfirmCloseModal from '../components/MyCatalogues/Modals/ConfirmCloseModal';
import PublishModal from '../components/MyCatalogues/Modals/PublishModal';
import EditPublishedCatalogModal from '../components/MyCatalogues/Modals/EditPublishedCatalogModal';
import ConfirmClosePublishedModal from '../components/MyCatalogues/Modals/ConfirmClosePublishedModal';
import ExistingDraftModal from '../components/MyCatalogues/Modals/ExistingDraftModal';
import ConfirmDeleteDraftModal from '../components/MyCatalogues/Modals/ConfirmDeleteDraftModal';
import ConfirmUnpublishCatalogModal from '../components/MyCatalogues/Modals/ConfirmUnpublishCatalogModal';
import ConfirmRepublishCatalogModal from '../components/MyCatalogues/Modals/ConfirmRepublishCatalogModal';
import ConfirmDeleteCatalogModal from '../components/MyCatalogues/Modals/ConfirmDeleteCatalogModal';
import ConfirmPublishChangesModal from '../components/MyCatalogues/Modals/ConfirmPublishChangesModal';

// Icons
import AddIcon from '@mui/icons-material/Add';
import LinkIcon from '@mui/icons-material/Link';
import { AnalyticsSharp, ListSharp } from '@mui/icons-material';
import HeaderComponent from '../components/Components/Headers/HeaderComponent';



const MyCatalogues = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { message: messageApi } = App.useApp();
    const location = useLocation();

    // Custom hooks
    const {
        catalogues,
        setCatalogues,
        loading,
        catalogueStats,
        cataloguesCache,
        fetchCatalogues
    } = useCatalogues();

    const {
        modalState,
        setModalState,
        currentStep,
        setCurrentStep,
        stepperKey,
        setStepperKey,
        activeMenu,
        setActiveMenu
    } = useModalState();

    const {
        draftFormData,
        setDraftFormData,
        currentDraftId,
        setCurrentDraftId,
        currentPublishedCatalogueId,
        setCurrentPublishedCatalogueId,
        isEditMode,
        setIsEditMode,
        isDraftSavable,
        mapDraftToFormData
    } = useDraftManagement();

    // Local state
    const [catalogueListType, setCatalogueListType] = useState('published');
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [publishDraftId, setPublishDraftId] = useState(null);
    const [deleteDraftId, setDeleteDraftId] = useState(null);
    const [deleteDraftName, setDeleteDraftName] = useState('');
    const [unpublishCatalogId, setUnpublishCatalogId] = useState(null);
    const [unpublishCatalogName, setUnpublishCatalogName] = useState('');
    const [republishCatalogId, setRepublishCatalogId] = useState(null);
    const [republishCatalogName, setRepublishCatalogName] = useState('');
    const [deleteCatalogId, setDeleteCatalogId] = useState(null);
    const [deleteCatalogName, setDeleteCatalogName] = useState('');
    const [publishChangesData, setPublishChangesData] = useState({
        catalogId: null,
        catalogName: '',
        draftId: null,
        isLoading: false
    });

    // State for modals and UI
    const [existingDraftModalVisible, setExistingDraftModalVisible] = useState(false);
    const [pendingPublishedCatalogueId, setPendingPublishedCatalogueId] = useState(null);
    const [pendingDraftId, setPendingDraftId] = useState(null);
    const [modifyCatalogFormInstance, setModifyCatalogFormInstance] = useState(null);
    const [isUpdatingExistingDraft, setIsUpdatingExistingDraft] = useState(false);
    const [currentExistingDraftId, setCurrentExistingDraftId] = useState(null);

    // State for user role and UI visibility
    const [userRole, setUserRole] = useState(null);
    const [showAddButton, setShowAddButton] = useState(false);

    // Funzione per determinare se l'utente può accedere alle azioni di modifica
    const canPerformActions = (role) => {
        const restrictedRoles = ['Modeller', 'ModellerSupervisor', 'Validator'];
        return !restrictedRoles.includes(role);
    };

    // Funzione per determinare se l'utente può accedere agli Analytics
    const canViewAnalytics = (role) => {
        const restrictedRoles = ['Modeller', 'ModellerSupervisor'];
        return !restrictedRoles.includes(role);
    };

    // Load user role on component mount
    useEffect(() => {
        const role = secureGetItem("role");
        setUserRole(role);

        // Set button visibility based on user role
        setShowAddButton(canPerformActions(role));
    }, []);

    // Handle scroll to specific catalogue
    useEffect(() => {
        const scrollToCatalogueId = location.state?.scrollToCatalogueId;
        if (scrollToCatalogueId && catalogues.length > 0 && !loading) {
            // Small delay to ensure DOM is rendered
            setTimeout(() => {
                const catalogueElement = document.getElementById(`catalogue-${scrollToCatalogueId}`);
                if (catalogueElement) {
                    catalogueElement.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center'
                    });
                    // Clear the state to prevent re-scrolling on subsequent renders
                    window.history.replaceState({}, document.title);
                }
            }, 300);
        }
    }, [location.state, catalogues, loading]);

    // Handle window resize for responsive layout
    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    // Global functions for MenuDropdown - Only set if user can perform actions
    useEffect(() => {
        if (!canPerformActions(userRole)) {
            // For restricted roles, don't expose global functions
            return;
        }

        // Expose refreshCatalogues function to window for use in MenuDropdown
        window.refreshCatalogues = () => fetchCatalogues(catalogueListType, true);

        // Expose notification function to window for use in MenuDropdown
        window.showNotification = ({ message, description, type }) => {
            if (messageApi) {
                messageApi[type]({
                    content: message,
                    description: description
                });
            }
        };
        window.handlePublishDraft = (catalogueId) => {
            setPublishDraftId(catalogueId);
            setCurrentStep(0);
            setStepperKey(Date.now());
            setModalState('publish');
        };

        window.handleDeleteDraft = (catalogueId) => {
            const draftToDelete = catalogues.find(cat => cat.id === catalogueId && cat.isDraft);
            if (draftToDelete) {
                setDeleteDraftId(catalogueId);
                setDeleteDraftName(draftToDelete.nameCatalog || 'Untitled');
                setModalState('deleteDraft');
            } else {
                messageApi.error('Draft not found');
            }
        };

        window.handleUnpublishCatalog = (catalogueId) => {
            const catalogToUnpublish = catalogues.find(cat => cat.id === catalogueId && !cat.isDraft);
            if (catalogToUnpublish) {
                setUnpublishCatalogId(catalogueId);
                setUnpublishCatalogName(catalogToUnpublish.nameCatalog || 'Untitled');
                setModalState('unpublishCatalog');
            } else {
                messageApi.error('Published catalog not found');
            }
        };

        window.handleRepublishCatalog = (catalogueId) => {
            const catalogToRepublish = catalogues.find(cat => cat.id === catalogueId && !cat.isDraft);
            if (catalogToRepublish) {
                setRepublishCatalogId(catalogueId);
                setRepublishCatalogName(catalogToRepublish.nameCatalog || 'Untitled');
                setModalState('republishCatalog');
            } else {
                messageApi.error('Published catalog not found');
            }
        };

        // Handle delete catalog (for published catalogs)
        window.handleDeleteCatalog = (catalogueId) => {
            const catalogToDelete = catalogues.find(cat => cat.id === catalogueId && !cat.isDraft);
            if (catalogToDelete) {
                setDeleteCatalogId(catalogueId);
                setDeleteCatalogName(catalogToDelete.nameCatalog || 'Untitled');
                setModalState('deleteCatalog');
            } else {
                messageApi.error('Published catalog not found');
            }
        };

        // Handle publish changes (for published catalogs with drafts)
        window.handlePublishChanges = (catalogueId) => {
            const catalogToUpdate = catalogues.find(cat => cat.id === catalogueId && !cat.isDraft);
            if (catalogToUpdate && catalogToUpdate.draftedCatalogRef) {
                setPublishChangesData({
                    catalogId: catalogueId,
                    catalogName: catalogToUpdate.nameCatalog || 'Untitled',
                    draftId: catalogToUpdate.draftedCatalogRef,
                    isLoading: false
                });
                setModalState('publishChanges');
            } else {
                messageApi.error('Published catalog with draft not found');
            }
        };

        if (catalogues) {
            window.catalogues = catalogues;
        }

        return () => {
            delete window.handlePublishDraft;
            delete window.catalogues;
            delete window.handleDeleteDraft;
            delete window.handleUnpublishCatalog;
            delete window.handleRepublishCatalog;
            delete window.handleDeleteCatalog;
            delete window.handlePublishChanges;
            delete window.refreshCatalogues;
            delete window.showNotification;
        };
    }, [catalogues, catalogueListType, fetchCatalogues, messageApi, userRole]);

    // Fetch catalogues on mount and when type changes
    useEffect(() => {
        fetchCatalogues(catalogueListType, true);
    }, []);

    useEffect(() => {
        if (cataloguesCache.lastFetched) {
            if (catalogueListType === 'draft') {
                // Filter out drafted catalogs that are referenced by published catalogs
                const publishedCatalogs = cataloguesCache['published'] || [];
                const draftedCatalogs = cataloguesCache['draft'] || [];

                // Get all draftedCatalogRef IDs from published catalogs
                const referencedDraftIds = publishedCatalogs
                    .filter(catalog => catalog.draftedCatalogRef)
                    .map(catalog => catalog.draftedCatalogRef);

                // Filter out drafts that are referenced by published catalogs
                const filteredDrafts = draftedCatalogs.filter(
                    draft => !referencedDraftIds.includes(draft.id)
                );

                setCatalogues(filteredDrafts);
            } else {
                setCatalogues(cataloguesCache[catalogueListType]);
            }
        }
    }, [catalogueListType, cataloguesCache, setCatalogues]);

    // Reset form state when modal is closed
    useEffect(() => {
        if (modalState === 'closed') {
            setCurrentDraftId(null);
            setCurrentPublishedCatalogueId(null);
        }
    }, [modalState, setCurrentDraftId, setCurrentPublishedCatalogueId]);

    // Event handlers
    const handleViewCatalogue = (id) => {
        navigate(`/my-catalogues/${id}`);
    };

    // Event handlers for menu toggle - Only if user can perform actions
    const handleMenuToggle = (catalogueId, event) => {
        if (!canPerformActions(userRole)) {
            return; // Don't show menu for restricted roles
        }

        // Calculate position for the menu
        if (event) {
            const rect = event.currentTarget.getBoundingClientRect();
            window.menuPosition = {
                top: `${rect.bottom + window.scrollY}px`,
                left: `${rect.right - 200}px`, // 200px is the width of the menu
                right: 'auto'
            };
        }
        setActiveMenu(activeMenu === catalogueId ? null : catalogueId);
    };

    const handleAddCatalogue = () => {
        if (!canPerformActions(userRole)) {
            messageApi.warning('You do not have permission to add catalogues');
            return;
        }

        setCurrentStep(0);
        setStepperKey(Date.now());
        setDraftFormData(null);
        setIsEditMode(false);
        setModalState('main');
    };

    const handleEditDraft = useCallback((catalogueId) => {
        if (!canPerformActions(userRole)) {
            messageApi.warning('You do not have permission to edit drafts');
            return;
        }

        const draft = catalogues.find(cat => cat.id === catalogueId);
        if (draft) {
            const mappedFormData = mapDraftToFormData(draft);
            setDraftFormData(mappedFormData);
            setCurrentDraftId(catalogueId);
            setIsEditMode(true);
            setCurrentStep(0);
            setStepperKey(Date.now());
            setModalState('main');
        } else {
            messageApi.error('Draft not found');
        }
    }, [catalogues, mapDraftToFormData, setDraftFormData, setCurrentDraftId, setIsEditMode, setCurrentStep, setStepperKey, setModalState, messageApi, userRole]);

    // Handler for continuing with the existing draft
    const handleContinueWithDraft = useCallback((draftId) => {
        if (!canPerformActions(userRole)) {
            messageApi.warning('You do not have permission to modify drafts');
            return;
        }

        // Look for the draft in the complete cache, not just the filtered list
        // This ensures we can find drafts that are referenced by published catalogs
        const allDrafts = cataloguesCache.draft || [];
        const draftCatalogue = allDrafts.find(cat => cat.id === draftId);

        if (draftCatalogue) {
            // Set up the form data from the draft
            const mappedFormData = mapDraftToFormData(draftCatalogue);
            setDraftFormData(mappedFormData);
            setCurrentDraftId(draftId);

            // Set the draft update states
            setIsUpdatingExistingDraft(true);
            setCurrentExistingDraftId(draftId);

            // Find the published catalog that references this draft
            const publishedCatalogs = cataloguesCache.published || [];
            const relatedPublishedCatalog = publishedCatalogs.find(cat => cat.draftedCatalogRef === draftId);

            if (relatedPublishedCatalog) {
                // If we found the related published catalog, use it to set up the proper context
                setCurrentPublishedCatalogueId(relatedPublishedCatalog.id);
                // Use modifyPublished modal state to ensure we use ModifyCatalogStepper
                setModalState('modifyPublished');
            } else {
                // Fallback to main modal if no related published catalog is found
                setIsEditMode(true);
                setModalState('main');
            }

            setCurrentStep(0);
            setStepperKey(Date.now());
        } else {
            console.error(`Draft with ID ${draftId} not found in cache:`, cataloguesCache);
            messageApi.error('Draft not found');
        }
    }, [cataloguesCache, mapDraftToFormData, setDraftFormData, setCurrentDraftId, setIsEditMode, setCurrentStep, setStepperKey, setModalState, setCurrentPublishedCatalogueId, messageApi, userRole]);

    const handleCreateNewDraft = useCallback(() => {
        if (!canPerformActions(userRole)) {
            messageApi.warning('You do not have permission to create drafts');
            return;
        }

        // Reset the draft update states since we're creating a new draft
        setIsUpdatingExistingDraft(false);
        setCurrentExistingDraftId(null);

        // Get the published catalog
        const catalogue = catalogues.find(cat => cat.id === pendingPublishedCatalogueId);

        if (catalogue) {
            // Set up the form data from the published catalog
            const mappedFormData = mapDraftToFormData(catalogue);
            setDraftFormData(mappedFormData);
            setCurrentPublishedCatalogueId(pendingPublishedCatalogueId);
            setCurrentStep(0);
            setStepperKey(Date.now());
            setModalState('modifyPublished');
        } else {
            messageApi.error('Catalogue not found');
        }
    }, [catalogues, mapDraftToFormData, setDraftFormData, pendingPublishedCatalogueId, setCurrentPublishedCatalogueId, setCurrentStep, setStepperKey, setModalState, messageApi, userRole]);

    const handleModifyPublishedCatalogue = useCallback((catalogueId) => {
        if (!canPerformActions(userRole)) {
            messageApi.warning('You do not have permission to modify catalogues');
            return;
        }

        const catalogue = catalogues.find(cat => cat.id === catalogueId);
        if (catalogue) {
            // Check if this published catalog already has a draft associated with it
            if (catalogue.draftedCatalogRef) {
                // Store the IDs for use in the modal
                setPendingPublishedCatalogueId(catalogueId);
                setPendingDraftId(catalogue.draftedCatalogRef);
                // Show the modal to let the user decide what to do
                setExistingDraftModalVisible(true);
            } else {
                // No existing draft, proceed normally
                // Reset the draft update states since we're creating a new draft
                setIsUpdatingExistingDraft(false);
                setCurrentExistingDraftId(null);

                const mappedFormData = mapDraftToFormData(catalogue);
                setDraftFormData(mappedFormData);
                setCurrentPublishedCatalogueId(catalogueId);
                setCurrentStep(0);
                setStepperKey(Date.now());
                setModalState('modifyPublished');
            }
        } else {
            messageApi.error('Catalogue not found');
        }
    }, [catalogues, mapDraftToFormData, setDraftFormData, setCurrentPublishedCatalogueId, setCurrentStep, setStepperKey, setModalState, messageApi, setPendingPublishedCatalogueId, setPendingDraftId, setExistingDraftModalVisible, userRole]);

    // Expose handleModifyPublishedCatalogue to window after it's defined - Only if user can perform actions
    useEffect(() => {
        if (canPerformActions(userRole)) {
            window.handleModifyPublishedCatalogue = handleModifyPublishedCatalogue;

            return () => {
                delete window.handleModifyPublishedCatalogue;
            };
        }
    }, [handleModifyPublishedCatalogue, userRole]);

    const handleConfirmDeleteDraft = async () => {
        try {
            if (!deleteDraftId) return;

            await deleteDraftedCatalogue(deleteDraftId);
            messageApi.success('Draft catalogue deleted successfully');
            setModalState('closed');
            setDeleteDraftId(null);
            setDeleteDraftName('');

            // Refresh the catalogues list
            fetchCatalogues(catalogueListType, true);
        } catch (error) {
            console.error('Error deleting draft catalogue:', error);
            messageApi.error('Error deleting draft: ' + (error.message || error));
        }
    };

    const handleConfirmUnpublishCatalog = async () => {
        try {
            if (!unpublishCatalogId) return;

            await updateCatalogStatus(unpublishCatalogId, 'Unpublished');
            messageApi.success('Catalog unpublished successfully');
            setModalState('closed');
            setUnpublishCatalogId(null);
            setUnpublishCatalogName('');

            // Refresh the catalogues list
            fetchCatalogues(catalogueListType, true);
        } catch (error) {
            console.error('Error unpublishing catalog:', error);
            messageApi.error('Error unpublishing catalog: ' + (error.message || error));
        }
    };

    const handleConfirmRepublishCatalog = async () => {
        try {
            if (!republishCatalogId) return;

            await updateCatalogStatus(republishCatalogId, 'Published');
            messageApi.success('Catalog republished successfully');

            // 📧 Notify catalog republication via Firebase Function
            try {
                // Cerca la subscription che contiene questo catalogo
                const subscriptionId = await getSubscriptionIdByCatalogueId(republishCatalogId);

                if (subscriptionId) {
                    await notifyNewCatalogPublished(republishCatalogId, subscriptionId);
                } else {
                    console.warn('⚠️ No subscription found for catalog, skipping republication notification');
                }
            } catch (notificationError) {
                console.error('❌ Failed to send republication notification:', notificationError);
                // Non bloccare la ripubblicazione se la notifica fallisce
                messageApi.warning('Catalog republished successfully, but notification email failed to send');
            }

            setModalState('closed');
            setRepublishCatalogId(null);
            setRepublishCatalogName('');

            // Refresh the catalogues list
            fetchCatalogues(catalogueListType, true);
        } catch (error) {
            console.error('Error republishing catalog:', error);
            messageApi.error('Error republishing catalog: ' + (error.message || error));
        }
    };

    // Handle confirmation of catalog deletion
    const handleConfirmDeleteCatalog = async () => {
        try {
            if (!deleteCatalogId) return;

            // Import the service function dynamically to avoid circular dependencies
            const { removeCatalogFromProfiles } = await import('../services/api/profileCatalogService');

            // Remove catalog from all user profiles
            await removeCatalogFromProfiles(deleteCatalogId);

            messageApi.success('Catalog deleted successfully');
            setModalState('closed');
            setDeleteCatalogId(null);
            setDeleteCatalogName('');

            // Refresh the catalogues list
            fetchCatalogues(catalogueListType, true);
        } catch (error) {
            console.error('Error deleting catalog:', error);
            messageApi.error('Error deleting catalog: ' + (error.message || error));
        }
    };

    // Handle confirmation of publishing changes from draft to catalog
    const handleConfirmPublishChanges = async () => {
        try {
            const { catalogId, draftId } = publishChangesData;
            if (!catalogId || !draftId) return;

            // Set loading state
            setPublishChangesData(prev => ({ ...prev, isLoading: true }));

            // Import the service function
            const { publishChangesFromDraft } = await import('../services/api/catalogueBaseService');

            // Update the published catalog with draft changes and clean up
            await publishChangesFromDraft(catalogId, draftId);

            messageApi.success('Changes published successfully');

            // 📧 Notify catalog changes publication via Firebase Function
            try {
                // Cerca la subscription che contiene questo catalogo
                const subscriptionId = await getSubscriptionIdByCatalogueId(catalogId);

                if (subscriptionId) {
                    await notifyNewCatalogPublished(catalogId, subscriptionId);
                } else {
                    console.warn('⚠️ No subscription found for catalog, skipping changes publication notification');
                }
            } catch (notificationError) {
                console.error('❌ Failed to send changes publication notification:', notificationError);
                // Non bloccare la pubblicazione se la notifica fallisce
                messageApi.warning('Changes published successfully, but notification email failed to send');
            }

            setModalState('closed');
            setPublishChangesData({
                catalogId: null,
                catalogName: '',
                draftId: null,
                isLoading: false
            });

            // Refresh the catalogues list
            fetchCatalogues(catalogueListType, true);
        } catch (error) {
            console.error('Error publishing changes:', error);
            messageApi.error('Error publishing changes: ' + (error.message || error));

            // Reset loading state
            setPublishChangesData(prev => ({ ...prev, isLoading: false }));
        }
    };

    const handleCloseModal = () => {
        setModalState('closeConfirm');
    };

    const handleShowSuccessModal = async (mappedData, catalogueId) => {

        // If catalogueId is provided, it means the catalogue was already saved successfully in CreateCatalogStepper
        if (catalogueId) {
            messageApi.success(isEditMode ? 'Catalogue updated successfully' : 'New catalogue created');
            setModalState('successConfirm');
            fetchCatalogues(catalogueListType, true);
            return;
        }

        // Fallback to old behavior for cases where saving is still done here
        const dataToSave = mappedData || draftFormData;

        if (isEditMode && currentDraftId) {
            try {
                await updateDraftedCatalogue(currentDraftId, dataToSave);
                messageApi.success('Catalogue updated successfully');
            } catch (err) {
                console.error('Error updating catalogue:', err);
                messageApi.error('Error updating catalogue: ' + (err.message || err));
                return; // Don't show success modal if there was an error
            }
        } else {
            try {
                await saveDraftedCatalogue(dataToSave);
                messageApi.success('New catalogue created');
            } catch (err) {
                console.error('Error creating catalogue:', err);
                messageApi.error('Error creating catalogue: ' + (err.message || err));
                return; // Don't show success modal if there was an error
            }
        }

        setModalState('successConfirm');
        fetchCatalogues(catalogueListType, true);
    };

    const getActionButtons = (catalogue) => {
        if (catalogue.isDraft) {
            // No action buttons for drafted catalogs as per requirements
            return [];
        } else {
            const buttons = [
                {
                    icon: <ListSharp />,
                    label: 'Assets List',
                    onClick: () => handleViewCatalogue(catalogue.id)
                },
                {
                    icon: <LinkIcon />,
                    label: 'Go To',
                    onClick: (e) => {
                        e.stopPropagation();
                        window.open(`https://${catalogue.webDomain}/?c=${catalogue.id}`, '_blank', 'noopener,noreferrer');
                    }
                }
            ];

            // Add Analytics button if user can view analytics (excludes Modeller and ModellerSupervisor)
            if (canViewAnalytics(userRole)) {
                buttons.splice(1, 0, {
                    icon: <AnalyticsSharp />,
                    label: 'Analytics',
                    onClick: () => {
                        navigate('/analytics-vtos');
                        dispatch(setSelectedCatalogues([catalogue]));
                    }
                });
            }

            return buttons;
        }
    };

    return (
        <div>
            <HeaderComponent
                title="My Catalogues"
                buttonText="Add catalogue"
                buttonIcon={<AddIcon />}
                onButtonClick={handleAddCatalogue}
                isVisible={showAddButton}
                centerContent={
                    <ToggleButtons
                        value={catalogueListType}
                        onChange={(value) => setCatalogueListType(value)}
                        options={[
                            { value: 'published', label: 'Published' },
                            {
                                value: 'draft',
                                label: (
                                    <span>
                                        Drafts
                                        {(() => {
                                            // Filter out drafts that are referenced by published catalogs
                                            const publishedCatalogs = cataloguesCache['published'] || [];
                                            const draftedCatalogs = cataloguesCache['draft'] || [];

                                            // Get all draftedCatalogRef IDs from published catalogs
                                            const referencedDraftIds = publishedCatalogs
                                                .filter(catalog => catalog.draftedCatalogRef)
                                                .map(catalog => catalog.draftedCatalogRef);

                                            // Filter out drafts that are referenced by published catalogs
                                            const filteredDrafts = draftedCatalogs.filter(
                                                draft => !referencedDraftIds.includes(draft.id)
                                            );

                                            const draftCount = filteredDrafts.length;

                                            return draftCount > 0 && (
                                                <div style={{
                                                    position: 'absolute',
                                                    top: '-0.5rem',
                                                    right: '-0.5rem',
                                                    backgroundColor: statusTokens.error,
                                                    color: "#fff",
                                                    minWidth: 'clamp(0.875rem, 1.5vw, 1.125rem)',
                                                    height: 'clamp(0.875rem, 1.5vw, 1.125rem)',
                                                    fontSize: 'clamp(0.5rem, 0.7vw, 0.625rem)',
                                                    borderRadius: '50%',
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                    padding: '0 3px'
                                                }}>
                                                    {draftCount > 10 ? '10+' : draftCount}
                                                </div>
                                            );
                                        })()}
                                    </span>
                                )
                            }
                        ]}
                        type="button"
                    />
                }
            />

            {loading ? (
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: 'calc(100vh - 25rem)'
                }}>
                    <Spin size="large" />
                </div>
            ) : catalogues.length > 0 ? (
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: windowWidth > 1366 ? 'repeat(2, 1fr)' : '1fr',
                    gap: '1rem'
                }}>
                    {catalogues.map((catalogue) => {
                        // Check if this published catalog has a draft in progress
                        const hasDraftInProgress = !catalogue.isDraft && catalogue.draftedCatalogRef;

                        return (
                            <div key={catalogue.id} id={`catalogue-${catalogue.id}`}>
                                <CardCatalog
                                    catalogue={catalogue}
                                    getQrCodeUrl={() => getQrCodeUrl(catalogue)}
                                    handleDownloadQrCode={() => handleDownloadQrCode(catalogue)}
                                    cardSectionHeight="50%"
                                    handleMenuToggle={handleMenuToggle}
                                    activeMenu={activeMenu}
                                    setActiveMenu={setActiveMenu}
                                    handleEditDraft={handleEditDraft}

                                    getStatCards={() => getStatCards(catalogueStats, catalogue)}
                                    getActionButtons={() => getActionButtons(catalogue)}
                                    hasDraftInProgress={hasDraftInProgress}
                                    canPerformActions={canPerformActions(userRole)}
                                />
                            </div>
                        );
                    })}
                </div>
            ) : (
                <SimpleCard>

                    <Empty
                        description={catalogueListType === 'published' ?
                            "You haven't published any catalogues yet" :
                            "You don't have any draft catalogues"}
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                    >
                    </Empty>
                </SimpleCard>
            )}

            {/* Modals - Only render if user can perform actions */}
            {canPerformActions(userRole) && (
                <>
                    <NewCatalogModal
                        isEditMode={isEditMode}
                        modalState={modalState}
                        handleCloseModal={handleCloseModal}
                        stepperKey={stepperKey}
                        currentStep={currentStep}
                        setCurrentStep={setCurrentStep}
                        draftFormData={draftFormData}
                        setDraftFormData={setDraftFormData}
                        currentDraftId={currentDraftId}
                        handleShowSuccessModal={handleShowSuccessModal}
                    />

                    <ConfirmCloseModal
                        modalState={modalState}
                        setModalState={setModalState}
                        isDraftSavable={isDraftSavable}
                        currentDraftId={currentDraftId}
                        draftFormData={draftFormData}
                        formInstance={modifyCatalogFormInstance} // Use the form instance for unified system
                        originalCatalogueId={currentPublishedCatalogueId} // Pass for published catalog modifications
                        fetchCatalogues={() => fetchCatalogues(catalogueListType, true)}
                        isEditMode={isEditMode}
                        messageApi={messageApi}
                        statusTokens={statusTokens}
                    />

                    <ConfirmClosePublishedModal
                        modalState={modalState}
                        setModalState={setModalState}
                        isDraftSavable={isDraftSavable}
                        currentPublishedCatalogueId={currentPublishedCatalogueId}
                        draftFormData={draftFormData}
                        fetchCatalogues={() => fetchCatalogues(catalogueListType, true)}
                        messageApi={messageApi}
                        statusTokens={statusTokens}
                        formInstance={modifyCatalogFormInstance} // Pass the form instance from EditPublishedCatalogModal
                        existingDraftId={currentExistingDraftId} // Pass the existing draft ID
                    />

                    <ConfirmationModal modalState={modalState} setModalState={setModalState} />

                    <PublishModal
                        modalState={modalState}
                        setModalState={setModalState}
                        fetchCatalogues={() => fetchCatalogues(catalogueListType, true)}
                        messageApi={messageApi}
                        statusTokens={statusTokens}
                        stepperKey={stepperKey}
                        currentStep={currentStep}
                        setCurrentStep={setCurrentStep}
                        publishDraftId={publishDraftId}
                        setPublishDraftId={setPublishDraftId}
                    />

                    <EditPublishedCatalogModal
                        modalState={modalState}
                        setModalState={setModalState}
                        fetchCatalogues={() => fetchCatalogues(catalogueListType, true)}
                        messageApi={messageApi}
                        stepperKey={stepperKey}
                        currentStep={currentStep}
                        setCurrentStep={setCurrentStep}
                        draftFormData={draftFormData}
                        currentPublishedCatalogueId={currentPublishedCatalogueId}
                        isUpdatingExistingDraft={isUpdatingExistingDraft}
                        existingDraftId={currentExistingDraftId}
                        onFormInstanceReady={(formInstance) => {
                            setModifyCatalogFormInstance(formInstance);
                        }}
                    />

                    {/* Modal for handling existing drafts */}
                    <ExistingDraftModal
                        isOpen={existingDraftModalVisible}
                        onClose={() => setExistingDraftModalVisible(false)}
                        publishedCatalogueId={pendingPublishedCatalogueId}
                        draftedCatalogRef={pendingDraftId}
                        onContinueWithDraft={handleContinueWithDraft}
                        onCreateNewDraft={handleCreateNewDraft}
                    />

                    {/* Modal for confirming draft deletion */}
                    <ConfirmDeleteDraftModal
                        isOpen={modalState === 'deleteDraft'}
                        onClose={() => setModalState('closed')}
                        onConfirm={handleConfirmDeleteDraft}
                        draftName={deleteDraftName}
                        statusTokens={statusTokens}
                    />

                    {/* Modal for confirming unpublish catalog */}
                    <ConfirmUnpublishCatalogModal
                        isOpen={modalState === 'unpublishCatalog'}
                        onClose={() => setModalState('closed')}
                        onConfirm={handleConfirmUnpublishCatalog}
                        catalogName={unpublishCatalogName}
                        primaryShades={statusTokens}
                    />

                    {/* Modal for confirming republish catalog */}
                    <ConfirmRepublishCatalogModal
                        isOpen={modalState === 'republishCatalog'}
                        onClose={() => setModalState('closed')}
                        onConfirm={handleConfirmRepublishCatalog}
                        catalogName={republishCatalogName}
                        primaryShades={statusTokens}
                    />

                    {/* Modal for confirming delete catalog */}
                    <ConfirmDeleteCatalogModal
                        isOpen={modalState === 'deleteCatalog'}
                        onClose={() => setModalState('closed')}
                        onConfirm={handleConfirmDeleteCatalog}
                        catalogName={deleteCatalogName}
                    />

                    {/* Modal for confirming publish changes */}
                    <ConfirmPublishChangesModal
                        isOpen={modalState === 'publishChanges'}
                        onClose={() => {
                            setModalState('closed');
                            setPublishChangesData(prev => ({ ...prev, isLoading: false }));
                        }}
                        onConfirm={handleConfirmPublishChanges}
                        catalogName={publishChangesData.catalogName}
                        isLoading={publishChangesData.isLoading}
                    />
                </>
            )}
        </div>
    );
};

export default MyCatalogues;